/**
 * Client-side auth API module — replaces auth.action.ts Server Actions.
 *
 * All functions use authFetch / authFetchJson for API calls.
 * No "use server" directive, no next/headers cookies, no revalidateTag.
 */

import { AUTH } from "@/shared/constants/backend-api"
import { authFetch, authFetchJson, setAccessToken } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { User } from "@/features/homeowners/types/backend/user.type"
import { SignupForm1ApiType } from "../types/signup.type"
import {
  LoginApiResponseData,
  SignupApiResponseData,
} from "../types/auth-actions.type"
import { RegistrationProgressResponseData } from "../types/response/registration-response.type"

// ---------------------------------------------------------------------------
// Signup
// ---------------------------------------------------------------------------

export async function signupUserStep1(
  data: SignupForm1ApiType,
): Promise<ApiResponse<SignupApiResponseData>> {
  return authFetchJson<SignupApiResponseData>(AUTH.registration.signup.step1, {
    method: "POST",
    body: JSON.stringify(data),
    skipAuth: true,
  })
}

export async function signupUserStep1Complete(
  data: SignupForm1ApiType,
): Promise<ApiResponse<SignupApiResponseData>> {
  return authFetchJson<SignupApiResponseData>(
    `${AUTH.registration.signup.step1}?complete=true`,
    {
      method: "POST",
      body: JSON.stringify(data),
      skipAuth: true,
    },
  )
}

// ---------------------------------------------------------------------------
// Registration progress
// ---------------------------------------------------------------------------

export async function getRegistrationProgress(
  email: string,
): Promise<ApiResponse<RegistrationProgressResponseData>> {
  return authFetchJson<RegistrationProgressResponseData>(
    AUTH.registration.progress(email),
    { skipAuth: true },
  )
}

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

export async function loginUser(
  email: string,
  password: string,
): Promise<ApiResponse<User>> {
  const res = await authFetchJson<LoginApiResponseData>(AUTH.login, {
    method: "POST",
    body: JSON.stringify({ email, password }),
    skipAuth: true,
  })

  if (!res.success) {
    return {
      success: false as const,
      status: res.status ?? 403,
      message: res.message || "Invalid email or password",
    }
  }

  // Store access token in-memory
  if (res.data?.accessToken) {
    setAccessToken(res.data.accessToken)
  }

  return {
    success: true as const,
    data: res.data.user,
  }
}

// ---------------------------------------------------------------------------
// OTP (email code login)
// ---------------------------------------------------------------------------

export async function sendLoginOtp(
  email: string,
): Promise<ApiResponse<{ message: string }>> {
  return authFetchJson<{ message: string }>(AUTH.send_otp, {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  })
}

export async function VerifyOtp(
  email: string,
  code: string,
): Promise<ApiResponse<any>> {
  const res = await authFetchJson<LoginApiResponseData>(AUTH.verify_otp, {
    method: "POST",
    body: JSON.stringify({ email, code }),
    skipAuth: true,
  })
  if (!res.success) return res

  if (res.data?.accessToken) {
    setAccessToken(res.data.accessToken)
  }

  return {
    success: true as const,
    data: res.data.user,
  }
}

// ---------------------------------------------------------------------------
// Password reset
// ---------------------------------------------------------------------------

export async function forgotPassword(
  email: string,
): Promise<ApiResponse<string>> {
  return authFetchJson<string>(AUTH.registration.forgotPassword, {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  })
}

export async function resetPassword(props: {
  email: string
  token: string
  password: string
}): Promise<ApiResponse<{ success: string }>> {
  return authFetchJson<{ success: string }>(
    AUTH.registration.resetPassword(props.email, props.token),
    {
      method: "POST",
      body: JSON.stringify({
        newPassword: props.password,
        token: props.token,
        email: props.email,
      }),
      skipAuth: true,
    },
  )
}

// ---------------------------------------------------------------------------
// Email existence check
// ---------------------------------------------------------------------------

export async function isEmailExists(
  email: string,
): Promise<ApiResponse<boolean> & { hasPassword?: boolean; provider?: string }> {
  const res = await authFetchJson<{
    exists: boolean
    hasPassword: boolean
    provider?: string
  }>(AUTH.checkEmail, {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  })

  if (!res.success) {
    if (res.status === 404) {
      return { success: true, data: false, hasPassword: false }
    }
    return { ...res, hasPassword: false }
  }

  return {
    success: true,
    data: res.data?.exists ?? false,
    hasPassword: res.data?.hasPassword ?? false,
    provider: res.data?.provider,
  }
}

// ---------------------------------------------------------------------------
// Google OAuth helpers (client-side redirect)
// ---------------------------------------------------------------------------

export function getGoogleAuthUrl(state?: string, loginHint?: string): string {
  const url = AUTH.google.base
  const params = new URLSearchParams()
  if (state) params.set("state", state)
  if (loginHint) params.set("login_hint", loginHint)
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}
